import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import '../containers/Home.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';

export function SearchAutocomplete({ value, onChange, suggestions, onSelectSuggestion, placeholder = 'Buscar eventos', className = '' }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const listboxId = 'search-autocomplete-listbox';

  const normalizedSuggestions = useMemo(() => {
    const list = (suggestions || []).map((s) =>
      typeof s === 'string' ? { label: s, isVenue: false } : s
    );
    const q = (value || '').toString().trim().toLowerCase();
    if (!q) return list; // show all, alphabetically (already sorted by builder)
    return list.filter((s) => s.label && s.label.toLowerCase().includes(q));
  }, [suggestions, value]);

  const handleChange = (e) => {
    onChange(e.target.value);
    setOpen(true);
    setActiveIndex(-1);
  };

  const selectItem = useCallback((item) => {
    if (!item) return;
    const label = typeof item === 'string' ? item : item.label;
    onChange(label);
    if (onSelectSuggestion) onSelectSuggestion(label);
    setOpen(false);
    setActiveIndex(-1);
    if (inputRef.current) inputRef.current.blur();
  }, [onChange, onSelectSuggestion]);

  const onKeyDown = (e) => {
    if (!open && ['ArrowDown', 'ArrowUp'].includes(e.key)) {
      setOpen(true);
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, normalizedSuggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        if (open && activeIndex >= 0) {
          e.preventDefault();
          selectItem(normalizedSuggestions[activeIndex]);
        } else {
          // Hide suggestions even if no selection
          setOpen(false);
          setActiveIndex(-1);
        }
        break;
      case 'Escape':
        if (value) {
          onChange('');
        }
        setOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!inputRef.current) return;
      if (!inputRef.current.parentElement.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  return (
    <div className={`search-autocomplete ${className}`}>
      <input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={() => { setOpen(false); setActiveIndex(-1); }}
        placeholder={placeholder}
        className="search-autocomplete__input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
      />
      {value && (
        <button className="search-autocomplete__clear" aria-label="Limpiar búsqueda" onClick={() => { onChange(''); setOpen(false); setActiveIndex(-1); }}>
          ×
        </button>
      )}
      {open && normalizedSuggestions.length > 0 && (
        <ul id={listboxId} role="listbox" className="search-autocomplete__list">
          {normalizedSuggestions.map((s, idx) => (
            <li
              key={`${s.label || s}-${idx}`}
              role="option"
              aria-selected={idx === activeIndex}
              className={`search-autocomplete__item ${idx === activeIndex ? 'is-active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectItem(s)}
            >
              {s.isVenue && (
                <span style={{ marginRight: '8px', color: '#e6007a' }}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </span>
              )}
              {s.label || s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

SearchAutocomplete.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectSuggestion: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
};


